import client from 'client'
import type {
  UnretireAssetAssetAssetIdUnretirePostMutationResponse,
  UnretireAssetAssetAssetIdUnretirePostPathParams,
  UnretireAssetAssetAssetIdUnretirePostQueryParams,
  UnretireAssetAssetAssetIdUnretirePost422,
} from '../../types/UnretireAssetAssetAssetIdUnretirePost.ts'
import type { UseMutationOptions } from '@tanstack/react-query'
import type { RequestConfig, ResponseConfig, ResponseErrorConfig } from 'client'
import { useMutation } from '@tanstack/react-query'

export const unretireAssetAssetAssetIdUnretirePostMutationKey = () => [{ url: '/asset/{asset_id}/unretire' }] as const

export type UnretireAssetAssetAssetIdUnretirePostMutationKey = ReturnType<typeof unretireAssetAssetAssetIdUnretirePostMutationKey>

/**
 * @description Restore a retired asset to active status.
 * @summary Unretire an asset
 * {@link /asset/:asset_id/unretire}
 */
export async function unretireAssetAssetAssetIdUnretirePost(
  asset_id: UnretireAssetAssetAssetIdUnretirePostPathParams['asset_id'],
  params?: UnretireAssetAssetAssetIdUnretirePostQueryParams,
  config: Partial<RequestConfig> & { client?: typeof client } = {},
) {
  const { client: request = client, ...requestConfig } = config

  const res = await request<UnretireAssetAssetAssetIdUnretirePostMutationResponse, ResponseErrorConfig<UnretireAssetAssetAssetIdUnretirePost422>, unknown>({
    method: 'POST',
    url: `/asset/${asset_id}/unretire`,
    params,
    ...requestConfig,
  })
  return res
}

/**
 * @description Restore a retired asset to active status.
 * @summary Unretire an asset
 * {@link /asset/:asset_id/unretire}
 */
export function useUnretireAssetAssetAssetIdUnretirePost(
  options: {
    mutation?: UseMutationOptions<
      ResponseConfig<UnretireAssetAssetAssetIdUnretirePostMutationResponse>,
      ResponseErrorConfig<UnretireAssetAssetAssetIdUnretirePost422>,
      { asset_id: UnretireAssetAssetAssetIdUnretirePostPathParams['asset_id']; params?: UnretireAssetAssetAssetIdUnretirePostQueryParams }
    >
    client?: Partial<RequestConfig> & { client?: typeof client }
  } = {},
) {
  const { mutation: mutationOptions, client: config = {} } = options ?? {}
  const mutationKey = mutationOptions?.mutationKey ?? unretireAssetAssetAssetIdUnretirePostMutationKey()

  return useMutation<
    ResponseConfig<UnretireAssetAssetAssetIdUnretirePostMutationResponse>,
    ResponseErrorConfig<UnretireAssetAssetAssetIdUnretirePost422>,
    { asset_id: UnretireAssetAssetAssetIdUnretirePostPathParams['asset_id']; params?: UnretireAssetAssetAssetIdUnretirePostQueryParams }
  >({
    mutationFn: async ({ asset_id, params }) => {
      return unretireAssetAssetAssetIdUnretirePost(asset_id, params, config)
    },
    mutationKey,
    ...mutationOptions,
  })
}